import { useEffect, useState } from "react";
import {
  ArrowLeft, Shield, Star, CheckCircle, ChevronDown,
  Tag, Lock, Zap, Clock, Smartphone, Award,
  AlertCircle, CreditCard, Building2, Wallet,
  ChevronRight, Info,
} from "lucide-react";

import { useLocation } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../../../config/api";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

// ─── DATA ─────────────────────────────────────────────────────────────────────


// EMI Plans — like reference's "Pay with Monthly Fees"
const ENROLL_PLANS = [
  { id:1, devices:10, label:"10 Devices", total:19990,  highlight:false },
  { id:2, devices:20, label:"20 Devices", total:39980,  highlight:false },
  { id:3, devices:45, label:"45 Devices", total:89955,  highlight:true  },
  { id:4, devices:60, label:"60 Devices", total:119940, highlight:false },
  { id:5, devices:90, label:"90 Devices", total:179910, highlight:false },
];

const PAYMENT_METHODS = [
  // { id:"debit",   label:"Debit Card",  icon:CreditCard  },
  // { id:"credit",  label:"Credit Card", icon:CreditCard  },
  // { id:"netbank", label:"Net Banking", icon:Building2   },
  { id:"wallet",  label:"Wallet",      icon:Wallet      },
  { id:"upi",     label:"UPI / PayTM", icon:Zap         },
];

// ─── ATOMS ────────────────────────────────────────────────────────────────────

function FeatureDot({ color = "#3B82F6" }) {
  return <span className="w-2 h-2 rounded-full shrink-0 mt-0.5" style={{ background:color }}/>;
}

function StarIcon() {
  return <Star size={12} fill="#FBBF24" color="#FBBF24" className="shrink-0 mt-0.5"/>;
}

function SectionStep({ num, label }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[12px] font-black shrink-0"
        style={{ background:"var(--color-primary)" }}>
        {num}
      </div>
      <h3 className="font-display font-bold text-[13px] uppercase tracking-widest"
        style={{ color:"var(--color-text-primary)" }}>
        {label}
      </h3>
    </div>
  );
}

function Divider() {
  return <div className="h-px my-4" style={{ background:"var(--color-border-light)" }}/>;
}

// ─── MOLECULES ────────────────────────────────────────────────────────────────

/** Plan card — dark blue gradient like current screenshot */
function PlanHeroCard({ plan }) {
  return (
    <div className="rounded-2xl p-5 relative overflow-hidden"
      style={{
        background:"linear-gradient(135deg,#1E3A8A 0%,#1D4ED8 60%,#3B82F6 100%)",
        boxShadow:"0 8px 32px rgba(37,99,235,0.35)",
      }}>
      <div className="absolute -right-8 -top-8 w-28 h-28 rounded-full opacity-10"
        style={{ background:"rgba(255,255,255,0.4)" }}/>
      <div className="relative z-10">
        <span className="text-[9px] font-black uppercase tracking-widest"
          style={{ color:"rgba(147,197,253,0.9)" }}>WARRANTY PLAN</span>
        <h2 className="text-[22px] font-black text-white font-display mt-0.5 mb-2">
          {plan.subtitle}
        </h2>
        <p className="text-[11px] font-body" style={{ color:"rgba(186,230,253,0.85)" }}>
          {plan.type}
        </p>
      </div>
    </div>
  );
}

/** Price + plan validity box */
function PriceBox({ plan, selectedPlan }) {
  const total = selectedPlan.total;
  const devices = selectedPlan.devices;
  return (
    <div className="rounded-2xl p-4 mb-3 text-center"
      style={{ border:"1px solid var(--color-border-light)", background:"var(--color-surface)" }}>
      <div className="flex items-baseline justify-center gap-1 mb-0.5">
        <span className="text-[28px] font-black font-display" style={{ color:"var(--color-text-primary)" }}>
          ₹{plan.pricePerPlan.toLocaleString("en-IN")}
        </span>
        <span className="text-[12px] text-muted font-body">per plan</span>
      </div>
      <div className="flex items-center justify-center gap-4 mt-3 text-[11px] font-body"
        style={{ color:"var(--color-text-secondary)" }}>
        <div className="flex flex-col items-center">
          <span className="text-muted text-[10px] uppercase tracking-wide">Plan Validity</span>
          <span className="font-bold font-display mt-0.5">{plan.planValidity}</span>
        </div>
        <div className="w-px h-8" style={{ background:"var(--color-border-light)" }}/>
        <div className="flex flex-col items-center">
          <span className="text-muted text-[10px] uppercase tracking-wide">Devices</span>
          <span className="font-bold font-display mt-0.5">{devices}</span>
        </div>
        <div className="w-px h-8" style={{ background:"var(--color-border-light)" }}/>
        <div className="flex flex-col items-center">
          <span className="text-muted text-[10px] uppercase tracking-wide">Total</span>
          <span className="font-bold font-display mt-0.5" style={{ color:"var(--color-primary)" }}>
            ₹{total.toLocaleString("en-IN")}
          </span>
        </div>
      </div>
    </div>
  );
}

/** Enroll plan selector — like EMI selector in reference */
function EnrollPlanSelector({ plans, selected, onSelect }) {
  return (
    <div className="pt-5">
      {/* <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-bold font-display" style={{ color:"var(--color-text-primary)" }}>
            Select Enroll Count
          </span>
          <Info size={13} className="text-muted cursor-pointer"/>
        </div>
        <button className="text-[11px] font-bold" style={{ color:"var(--color-primary)" }}>
          × Deselect
        </button>
      </div> */}

      {/* <div className="grid grid-cols-3 gap-2 mb-3">
        {plans.map(plan => {
          const isSel = selected.id === plan.id;
          return (
            <button key={plan.id} onClick={() => onSelect(plan)}
              className="flex flex-col items-center py-3 px-2 rounded-xl border-2 transition-all duration-150
                cursor-pointer text-center"
              style={{
                borderColor:  isSel ? "var(--color-primary)"     : "var(--color-border-light)",
                background:   isSel ? "var(--color-info-surface)" : "var(--color-surface)",
                boxShadow:    isSel ? "0 0 0 3px rgba(37,99,235,0.12)" : "none",
              }}>
              <span className="text-[15px] font-black font-display leading-none"
                style={{ color: isSel ? "var(--color-primary)" : "var(--color-text-primary)" }}>
                ₹{(plan.total / plan.devices).toLocaleString("en-IN")}
              </span>
              <span className="text-[9px] font-body mt-0.5" style={{ color:"var(--color-text-muted)" }}>
                per device
              </span>
              <span className="text-[9px] font-bold font-display mt-1"
                style={{ color: isSel ? "var(--color-primary)" : "var(--color-text-secondary)" }}>
                {plan.label}
              </span>
            </button>
          );
        })}
      </div> */}

      {/* Info note */}
      <div className="flex items-start gap-2 p-2.5 rounded-xl"
        style={{ background:"var(--color-info-surface)", border:"1px solid #BFDBFE" }}>
        <Info size={12} className="text-primary shrink-0 mt-0.5"/>
        <p className="text-[10px] font-body" style={{ color:"var(--color-primary)" }}>
          Plan validity is 30 days from purchase date. All devices enrolled within this window.
        </p>
      </div>
    </div>
  );
}

/** Billing Summary panel — right column like reference */
function BillingSummary({ plan, selectedPlan, paymentMethod, onMethodChange, walletAllowed, walletBalance, handlePayment, handleWalletPayment, setShowWalletModal, isRetailerMobile  }) {
  const [coupon, setCoupon] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const discount     = couponApplied ? 500 : 0;
  


  return (
    <div
      className={`flex flex-col gap-4 rounded-2xl ${
        isRetailerMobile ? "glass" : "bg-white shadow-lg"
      }`}
    >

      {/* Billing card */}
      <div className="card p-5">
        <h3 className="font-display font-bold text-[14px] mb-4"
          style={{ color:"var(--color-text-primary)" }}>
          Billing Summary
        </h3>

        <div className="flex flex-col gap-2.5 mb-4">
          {[
            { label:`Price`,
              val:`₹${plan.pricePerPlan.toLocaleString("en-IN")}`, bold:false },

              // { label:`CGST`,
              // val:`₹${0.00}`, bold:false },
              // { label:`SGST`,
              // val:`₹${0.00}`, bold:false },
            // { label:"Discount", val: discount ? `-₹${discount}` : "-₹0", bold:false,
            //   color: discount ? "var(--color-success)" : "var(--color-text-muted)" },
          ].map(({ label, val, bold, color }) => (
            <div key={label} className="flex items-center justify-between">
              <span className="text-[12px] font-body" style={{ color:"var(--color-text-secondary)" }}>{label}</span>
              <span className="text-[12px] font-bold font-display" style={{ color: color || "var(--color-text-primary)" }}>
                {val}
              </span>
            </div>
          ))}

          <Divider/>

          <div className="flex items-center justify-between">
            <span className="text-[12px] font-bold font-display" style={{ color:"var(--color-text-primary)" }}>
              Amount to be paid
            </span>
            <span className="text-[18px] font-black font-display" style={{ color:"var(--color-text-primary)" }}>
              ₹{plan.pricePerPlan.toLocaleString("en-IN")}
            </span>
          </div>
        </div>

        {/* Coupon */}
        {/* <div className="flex gap-2 mb-4">
          <div className="flex-1 relative">
            <Tag size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted"/>
            <input value={coupon} onChange={e => setCoupon(e.target.value)}
              placeholder="Have a discount code?"
              className="w-full pl-8 pr-3 py-2.5 text-[12px] font-body rounded-xl outline-none"
              style={{ border:"1px solid var(--color-border)", background:"var(--color-surface-2)", color:"var(--color-text-primary)" }}/>
          </div>
          <button onClick={() => coupon && setCouponApplied(!couponApplied)}
            className="btn btn-primary px-4 py-2 rounded-xl text-[12px]">
            Apply
          </button>
        </div> */}

        {/* First EMI highlight */}
        <div
            className="flex items-center justify-between p-3 rounded-xl mb-1"
            style={{
              background: "var(--color-info-surface)",
              border: "1px solid #BFDBFE",
            }}
          >
            {/* LEFT */}
            <div>
              <p
                className="text-[12px] font-bold font-display"
                style={{ color: "var(--color-primary)" }}
              >
                Wallet Balance
              </p>

              <p
                className="text-[10px] font-body"
                style={{ color: "var(--color-text-muted)" }}
              >
                {walletAllowed
                  ? "Sufficient balance available"
                  : "Low balance – please recharge"}
              </p>
            </div>

            {/* RIGHT */}
            <div className="text-right">
              <p
                className="text-[16px] font-black font-display"
                style={{
                  color: walletAllowed
                    ? "var(--color-primary)"
                    : "#EF4444", // red when low
                }}
              >
                ₹{walletBalance?.toLocaleString("en-IN")}
              </p>

              <p
                className="text-[9px] font-bold uppercase tracking-wide"
                style={{
                  color: walletAllowed
                    ? "var(--color-primary)"
                    : "#EF4444",
                }}
              >
                {walletAllowed ? "Available" : "Insufficient"}
              </p>
            </div>
          </div>

        {!walletAllowed && (
          <div className="mt-2 text-[11px] text-red-500 font-medium">
            ⚠ You have low wallet balance. Please recharge to use wallet payment.
          </div>
        )}
      </div>

      {/* Payment method */}
      <div className="card p-5">
        <h3 className="font-display font-bold text-[13px] mb-3"
          style={{ color:"var(--color-text-primary)" }}>
          Select a Payment Method
        </h3>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {PAYMENT_METHODS.map(({ id, label, icon:Icon }) => {
            const isWallet = id === "wallet";
            const isDisabled = isWallet && !walletAllowed;

            return (
              <button
                key={id}
                disabled={isDisabled}
                onClick={() => {
                  if (isDisabled) return;

                  onMethodChange(id);

                  // 🔥 WALLET FLOW
                  if (id === "wallet") {
                    setShowWalletModal(true); // open modal
                  }

                  // 🔥 UPI FLOW (existing Razorpay)
                  if (id === "upi") {
                    handlePayment(plan.pricePerPlan);
                  }
                }}
                className="flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-[12px] font-bold
                  font-display transition-all duration-150"
                style={{
                  borderColor: isDisabled
                    ? "#E5E7EB"
                    : paymentMethod === id
                    ? "var(--color-primary)"
                    : "var(--color-border-light)",

                  background: isDisabled
                    ? "#F3F4F6"
                    : paymentMethod === id
                    ? "var(--color-info-surface)"
                    : "var(--color-surface)",

                  color: isDisabled
                    ? "#9CA3AF"
                    : paymentMethod === id
                    ? "var(--color-primary)"
                    : "var(--color-text-secondary)",

                  cursor: isDisabled ? "not-allowed" : "pointer",
                  opacity: isDisabled ? 0.6 : 1,
                }}
              >
                <Icon size={13}/>
                {label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-center gap-1.5 mt-3">
          <Lock size={11} className="text-muted"/>
          <span className="text-[10px] text-muted font-body">Secure Checkout</span>
        </div>
      </div>

      
    </div>
  );
}


function BillingSummary1({ plan, selectedPlan, paymentMethod, onMethodChange, walletAllowed, walletBalance, handlePayment, handleWalletPayment, setShowWalletModal, handleOfferSubscription, isRetailerMobile, loading }) {
  const mrp = Number(plan?.pricePerPlan || 0);

  const discountPrice =
    plan?.discount_price == null ? 0 : Number(plan.discount_price);

  const discount =
    discountPrice > 0 ? mrp - discountPrice : mrp;

  const payableAmount =
    discountPrice > 0 ? discountPrice : 0;

  const showPaymentSection =
    plan?.discount_price != null && Number(plan?.discount_price) > 0;

  return (
   <div className={`flex flex-col gap-5 rounded-2xl p-1 transition-all duration-300 ${
      isRetailerMobile 
        ? "glass backdrop-blur-md border border-white/20" 
        : "bg-slate-50/50 shadow-xl shadow-slate-100/40 border border-slate-100"
    }`}>

      {/* ── BILLING CARD ── */}
      {/* <div className=" rounded-2xl border border-slate-100/80 shadow-sm overflow-hidden"> */}
        
        {/* Header strip */}
        <div className="px-6 pt-5 pb-4 border-b border-slate-50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-blue-50/70 border border-blue-100/50 flex items-center justify-center shadow-inner">
              <span className="text-sm">🧾</span>
            </div>
            <div>
              <h3 className="font-display font-bold text-[15px] tracking-tight" style={{ color: "var(--color-text-primary)" }}>
                Billing Summary
              </h3>
              <p className="text-[11px] text-slate-400 font-body mt-0.5">Review your order details</p>
            </div>
          </div>
        </div>

        <div className="p-6 flex flex-col gap-3.5">
          {/* Price row */}
          <div className="flex items-center justify-between py-2.5 px-4 rounded-xl bg-slate-50/60 hover:bg-slate-50 transition-colors border border-slate-100/40">
            <div className="flex items-center gap-2.5">
              <div className="w-1.5 h-1.5 rounded-full bg-slate-400" />
              <span className="text-[13px] font-medium font-body" style={{ color: "var(--color-text-secondary)" }}>Base Price</span>
            </div>
            <span className="text-[14px] font-bold font-display tracking-tight text-slate-700">
              ₹{mrp.toLocaleString("en-IN")}
            </span>
          </div>

          {/* Discount row */}
          <div className="flex items-center justify-between py-2.5 px-4 rounded-xl bg-emerald-50/40 border border-emerald-100/70 hover:bg-emerald-50/60 transition-colors">
            <div className="flex items-center gap-2.5">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[13px] font-semibold font-body text-emerald-700">Special Discount</span>
            </div>
            <span className="text-[14px] font-bold font-display text-emerald-600">
              -₹{discount.toLocaleString("en-IN")}
            </span>
          </div>

          {/* Clean Performance-Friendly Divider Line */}
          <div className="my-1 border-t-2 border-dashed border-slate-200/70 w-full" />

          {/* Amount to pay */}
          <div className="flex items-center justify-between px-4 py-3.5 rounded-xl bg-gradient-to-br from-blue-50/60 via-indigo-50/40 to-indigo-50/60 border border-blue-100/60 shadow-inner">
            <div>
              <p className="text-[13px] font-bold font-display tracking-tight" style={{ color: "var(--color-text-primary)" }}>
                Total Payable Amount
              </p>
              <p className="text-[10px] text-indigo-400/90 font-body mt-0.5 font-medium">Inclusive of all local taxes</p>
            </div>
            <div className="text-right">
              {payableAmount === 0 ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-600 px-3 py-1 rounded-full border border-emerald-200 shadow-sm animate-bounce">
                    🎁 FREE
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-end">
                  <span className="text-[24px] font-black font-display tracking-tight leading-none" style={{ color: "var(--color-text-primary)" }}>
                    ₹{payableAmount.toLocaleString("en-IN")}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Wallet balance block */}
          {showPaymentSection && (
            <>
              <div className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-200 ${
                walletAllowed
                  ? "bg-blue-50/30 border-blue-200/70 shadow-sm"
                  : "bg-rose-50/30 border-rose-200/70 shadow-sm"
              }`}>
                {/* Left */}
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg shadow-sm transition-colors ${
                    walletAllowed ? "bg-blue-100/80 text-blue-600" : "bg-rose-100/80 text-rose-600"
          }`}>
                    💰
                  </div>
                  <div>
                    <p className="text-[13px] font-bold font-display" style={{ color: "var(--color-primary)" }}>
                      Wallet Balance
                    </p>
                    <p className="text-[11px] font-body mt-0.5 font-medium" style={{ color: "var(--color-text-muted)" }}>
                      {walletAllowed ? "Sufficient funds available" : "Low funds – action required"}
                    </p>
                  </div>
                </div>

                {/* Right */}
                <div className="text-right">
                  <p className={`text-[18px] font-black font-display tracking-tight leading-none mb-1 ${walletAllowed ? "text-blue-600" : "text-rose-600"}`}>
                    ₹{walletBalance?.toLocaleString("en-IN")}
                  </p>
                  <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md border ${
                    walletAllowed
                      ? "bg-blue-50 text-blue-600 border-blue-200/50"
                      : "bg-rose-50 text-rose-600 border-rose-200/50"
                  }`}>
                    {walletAllowed ? "✓ Available" : "✗ Insufficient"}
                  </span>
                </div>
              </div>

              {!walletAllowed && (
                <div className="flex items-start gap-2.5 px-4 py-3 rounded-xl bg-rose-50/50 border border-rose-200/60 animate-fade-in">
                  <span className="text-sm mt-0.5">⚠️</span>
                  <p className="text-[11px] text-rose-600 font-medium leading-relaxed">
                    Your balance is too low for this transaction. Please recharge to check out using your digital wallet.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      {/* </div> */}

      {/* ── PAYMENT METHOD CARD ── */}
      {showPaymentSection && (
        <div className="bg-white rounded-2xl border border-slate-100/80 shadow-sm overflow-hidden">
          
          {/* Header */}
          <div className="px-6 pt-5 pb-4 border-b border-slate-50">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-purple-50/70 border border-purple-100/50 flex items-center justify-center shadow-inner">
                <span className="text-sm">💳</span>
              </div>
              <div>
                <h3 className="font-display font-bold text-[14px] tracking-tight" style={{ color: "var(--color-text-primary)" }}>
                  Select Payment Method
                </h3>
                <p className="text-[11px] text-slate-400 font-body mt-0.5">Choose your preferred safe option</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-2 gap-3.5 mb-5">
              {PAYMENT_METHODS.map(({ id, label, icon: Icon }) => {
                const isWallet = id === "wallet";
                const isDisabled = isWallet && !walletAllowed;
                const isSelected = paymentMethod === id;

                return (
                  <button
                    key={id}
                    disabled={isDisabled}
                    onClick={() => {
                      if (isDisabled) return;
                      onMethodChange(id);
                      if (id === "wallet") setShowWalletModal(true);
                      if (id === "upi") handlePayment(plan.pricePerPlan);
                    }}
                    className={`relative flex flex-col sm:flex-row items-center justify-center gap-2.5 py-3.5 px-3 rounded-xl border-2 text-[13px] font-bold font-display transition-all duration-200 overflow-hidden group
                      ${isDisabled
                        ? "border-slate-100 bg-slate-50 text-slate-400 cursor-not-allowed opacity-50"
                        : isSelected
                          ? "border-blue-600 bg-blue-50/50 text-blue-700 shadow-md shadow-blue-100/50"
                          : "border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:bg-blue-50/20 hover:text-blue-600"
                      }`}
                  >
                    {isSelected && (
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-indigo-500/5 animate-pulse" />
                    )}
                    <Icon size={15} className={`transition-transform duration-200 ${!isDisabled && "group-hover:scale-110"}`} />
                    <span className="relative z-10 tracking-tight">{label}</span>
                    
                    {isSelected && (
                      <span className="absolute top-2 right-2 w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center shadow-sm">
                        <svg width="9" height="9" viewBox="0 0 8 8" fill="none">
                          <path d="M1.5 4L3 5.5L6.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Secure badge */}
            <div className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-slate-50 border border-slate-100/80 shadow-inner">
              <Lock size={12} className="text-slate-400" />
              <span className="text-[11px] text-slate-400 font-medium font-body tracking-wide">256-bit Bank-Grade Secure Checkout</span>
              <div className="flex gap-1 ml-1.5 opacity-70">
                {["🔒","✓"].map((s, i) => (
                  <span key={i} className="text-[10px] text-slate-400">{s}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── FREE SUBSCRIPTION BUTTON ── */}
      {!showPaymentSection && (
        <div className="p-2">
          <motion.button
            onClick={handleOfferSubscription}
            disabled={loading}
            whileHover={!loading ? {
              scale: 1.02,
              boxShadow: "0 10px 30px rgba(16,185,129,0.35)"
            } : {}}
            whileTap={!loading ? { scale: 0.98 } : {}}
            className="
              relative overflow-hidden w-full py-4 rounded-xl
              font-bold text-white
              bg-gradient-to-r from-emerald-600 via-teal-500 to-emerald-600
              shadow-lg shadow-emerald-100
              disabled:opacity-70 disabled:cursor-not-allowed
            "
          >
            {!loading && (
              <motion.div
                animate={{ x: ["-200%", "300%"] }}
                transition={{
                  duration: 2.2,
                  repeat: Infinity,
                  ease: "linear",
                  repeatDelay: 1.5,
                }}
                className="absolute top-0 left-0 h-full w-[40%]
                bg-gradient-to-r from-transparent via-white/30 to-transparent
                skew-x-[-20deg] pointer-events-none"
              />
            )}

            <span className="relative z-10 flex items-center justify-center gap-2 text-[14px] font-display tracking-wide uppercase">
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Activating Subscription...
                </>
              ) : (
                <>🎁 Activate Free Subscription</>
              )}
            </span>
          </motion.button>
        </div>
      )}

    </div>
  );
}

/** Plan overview section — matches current screenshot's section 1 */
function PlanOverviewSection({isRetailerMobile, plan }) {
  const rows = [
    { label:"Plan",     val:plan.type,            bold:true  },
    { label:"Plan Validity", val:plan.planValidity, bold:true  },
    { label:"Category/s",           val:`${plan.category}`, bold:true },
    { label:"Device Enroll",       val:plan.deviceenroll,       bold:true  },
  ];
  return (
    <div
      className={`rounded-2xl p-4 mb-3 ${
        isRetailerMobile ? "glass" : "bg-white shadow-lg"
      }`} style={{ border:"1px solid var(--color-border-light)" }}>
      <SectionStep num="1" label="Plan Overview"/>
      <div className="grid grid-cols-2 gap-x-8 gap-y-3">
        {rows.map(({ label, val, bold }) => (
          <div key={label} className="flex items-center justify-between border-b pb-2"
            style={{ borderColor:"var(--color-border-light)" }}>
            <span className="text-[11px] font-body" style={{ color:"var(--color-text-secondary)" }}>{label}</span>
            <span className={`text-[12px] font-display ${bold ? "font-bold" : "font-medium"}`}
              style={{ color:"var(--color-text-primary)" }}>{val}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Features section — what's included */
function FeaturesSection({ isRetailerMobile, plan }) {
  const half = Math.ceil(plan.features.length / 2);
  const left = plan.retailer_benifits.slice(0, half);
  const right = plan.retailer_benifits.slice(half);
  return (
    <div
      className={`rounded-2xl p-4 ${
        isRetailerMobile ? "glass" : "bg-white shadow-lg"
      }`} style={{ border:"1px solid var(--color-border-light)" }}>
      <SectionStep num="2" label="Plan Coverage & Features"/>
      <p className="text-[10px] font-black uppercase tracking-widest mb-3"
        style={{ color:"var(--color-warning)" }}>
        WHAT'S INCLUDED (FEATURES)
      </p>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2 mb-4">
        {[...left, ...right].map((f, i) => (
          <div key={i} className="flex items-start gap-2">
            <StarIcon/>
            <span className="text-[11px] font-body" style={{ color:"var(--color-text-secondary)" }}>{f}</span>
          </div>
        ))}
      </div>

      {/* <p className="text-[10px] font-black uppercase tracking-widest mb-3"
        style={{ color:"var(--color-primary)" }}>
        SERVICE COVERAGE
      </p>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2">
        {plan.serviceCoverage.map((s, i) => (
          <div key={i} className="flex items-start gap-2">
            <FeatureDot color={i%2===0?"#3B82F6":"#06B6D4"}/>
            <span className="text-[11px] font-body" style={{ color:"var(--color-text-secondary)" }}>{s}</span>
          </div>
        ))}
      </div> */}
    </div>
  );
}



function SkeletonBox({ className }) {
  return (
    <div
      className={`animate-pulse bg-slate-200 rounded ${className}`}
    />
  );
}

function OrderSummarySkeleton() {
  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-6">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">

        {/* LEFT SIDE */}
        <div className="lg:col-span-8 flex flex-col gap-4">

          {/* Hero Card */}
          <div className="bg-white rounded-2xl p-5 shadow">
            <SkeletonBox className="h-4 w-32 mb-3" />
            <SkeletonBox className="h-6 w-60 mb-2" />
            <SkeletonBox className="h-3 w-80" />
          </div>

          {/* Overview */}
          <div className="bg-white rounded-2xl p-4 shadow">
            <SkeletonBox className="h-4 w-40 mb-4" />

            <div className="grid grid-cols-2 gap-4">
              {[...Array(4)].map((_, i) => (
                <SkeletonBox key={i} className="h-4 w-full" />
              ))}
            </div>
          </div>

          {/* Features */}
          <div className="bg-white rounded-2xl p-4 shadow">
            <SkeletonBox className="h-4 w-52 mb-4" />

            <div className="grid grid-cols-2 gap-3">
              {[...Array(6)].map((_, i) => (
                <SkeletonBox key={i} className="h-3 w-full" />
              ))}
            </div>
          </div>

          {/* Info box */}
          <div className="bg-white rounded-2xl p-4 shadow">
            <SkeletonBox className="h-3 w-full" />
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="lg:col-span-4">
          <div className="bg-white rounded-2xl p-5 shadow flex flex-col gap-4">
            <SkeletonBox className="h-4 w-40" />

            {[...Array(3)].map((_, i) => (
              <SkeletonBox key={i} className="h-3 w-full" />
            ))}

            <SkeletonBox className="h-10 w-full rounded-xl" />
            <SkeletonBox className="h-10 w-full rounded-xl" />
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function OrderSummary({ onBack }) {
  const [selectedPlan,   setSelectedPlan]   = useState(ENROLL_PLANS[2]); // 45 devices default
  const [paymentMethod,  setPaymentMethod]  = useState("credit");

  
  const [walletPaymentLoading, setWalletPaymentLoading] = useState(false);

  const location = useLocation();
  const company_product_id = location.state?.company_product_id;
  console.log("company product id: ", company_product_id);

  const [productData, setProductData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [walletBalance, setWalletBalance] = useState(0);
  const [walletAllowed, setWalletAllowed] = useState(false);
  const [userId, setUserId] = useState(null);

  // payment 

  const [keyLoading, setKeyLoading] = useState(false);
  const [paymentKeys, setPaymentKeys] = useState({
      key_id: "",
      key_secret: ""
    });
  
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);

  const [showWalletModal, setShowWalletModal] = useState(false);
  
  const authUserString = localStorage.getItem("authUserData");
  const authUser = JSON.parse(authUserString);

  const navigate = useNavigate();


  useEffect(() => {
    const id = authUser?.id;
    setUserId(id);
  }, []);


  useEffect(() => {
    async function fetchProductDetails() {
      if (!company_product_id) return;

      setLoading(true);
      try {
        const res = await axios.get(
          `${BASE_URL}warranty/company-products`,
          {
            params: {
              company_id: authUser?.company_id, // or dynamic if needed
              company_product_id: company_product_id,
            },
          }
        );

        const companyBlock = res.data?.data?.[0];
        const selected = companyBlock?.products?.[0];

        if (!selected) return;

        const finalProduct = {
          company_product_id: selected.company_product_id,
          margin: selected.margin,
          ...selected.product,
        };

        setProductData(finalProduct);

      } catch (error) {
        console.error("Error fetching product details", error);
      } finally {
        setLoading(false);
      }
    }

    fetchProductDetails();
  }, [company_product_id]);


  const PLAN = productData
  ? {
      type: productData.product_type,
      subtitle: productData.name,
      pricePerPlan: Number(productData.mrp),
      planValidity: `${productData.sub_val_days} Days`,
      enrollCount: productData.enroll_max,
      claims: productData.claims,
      productValidity: `${productData.validity} Days`,
      priceType: productData.is_percent ? "Percent" : "Fixed",
      deviceenroll: productData.enroll_max,
      category: productData.categories?.[0].name,

      features: productData.features
        ? productData.features.split("✔").map(f => f.trim()).filter(Boolean)
        : [],

      serviceCoverage: productData.coverage
        ? productData.coverage.split(".").map(c => c.trim()).filter(Boolean)
        : [],

      retailer_benifits: productData.retailer_benifits
        ? productData.retailer_benifits.split(".").map(c => c.trim()).filter(Boolean)
        : [],
        show_popup: productData.show_popup
    }
  : null;

  useEffect(() => {

    async function fetchUser() {

      if (
        !userId ||
        !productData
      ) return;

      try {

        const response =
          await axios.get(
            `${BASE_URL}common/get-users`,
            {
              params: {
                id: userId,
              },

              headers: {
                Accept:
                  "application/json",

                "Content-Type":
                  "application/json",
              },
            }
          );

        const user =
          response.data?.data?.[0];

        if (!user)
          return;

        const balance =
          Number(
            user.wallet_balance || 0
          );

        const price =
          Number(
            productData.mrp || 0
          );

        setWalletBalance(
          balance
        );

        if (
          balance >= price
        ) {

          setWalletAllowed(
            true
          );

        } else {

          setWalletAllowed(
            false
          );
        }

      } catch (err) {

        console.error(
          "User fetch error",
          err
        );
      }
    }

    fetchUser();

  }, [userId, productData]);


const fetchPaymentKeys = async () => {

  try {

    setKeyLoading(true);

    const response =
      await axios.get(
        `${BASE_URL}payment/keys`,
        {
          headers: {
            Accept:
              "application/json",

            "Content-Type":
              "application/json",
          },
        }
      );

    const data =
      response.data;

    if (
      Array.isArray(data) &&
      data.length > 0
    ) {

      setPaymentKeys({
        key_id:
          data[0].key_id,

        key_secret:
          data[0].key_secret,
      });

    } else {

      console.error(
        "Invalid keys response",
        data
      );
    }

  } catch (err) {

    console.error(
      "Failed to fetch payment keys",
      err
    );

  } finally {

    setKeyLoading(false);

  }
};

useEffect(() => {
    fetchPaymentKeys();
}, []);

  const handleWalletPayment = async () => {

    try {

      setWalletPaymentLoading(
        true
      );

      const payload = {

        package_id:
          productData?.id,

        package_name:
          productData?.name,

        company_package_id:
          productData?.company_product_id,

        company_id:
          authUser?.company_id,

        retailer_id:
          authUser?.id,

        enroll_max:
          productData?.enroll_max,

        balance:
          walletBalance,

        amount:
          productData?.mrp,
      };

      const response =
        await axios.post(
          `${BASE_URL}warranty/buy-subscription-with-credit`,
          payload,
          {
            headers: {
              Accept:
                "application/json",

              "Content-Type":
                "application/json",
            },
          }
        );

      const data =
        response.data;

      toast.success(
        "Subscription purchased using wallet 🎉"
      );

      const updatedAuthUser = {
      ...authUser,
      
      is_subscribed: 1,
    };

    localStorage.setItem(
      "authUserData",
      JSON.stringify(updatedAuthUser)
    );

      navigate(
        "/retproduct",
        {
          state: {
            activeTab:
              "subscribed",
          },
        }
      );

    } catch (err) {

      console.error(
        err
      );

      toast.error(
        err?.response?.data
          ?.message ||
        "Something went wrong!"
      );

    } finally {

      setWalletPaymentLoading(
        false
      );

    }
  };

  const handleOfferSubscription = async () => {
  try {
    setWalletPaymentLoading(true);

    const payload = {
      package_id: productData?.id,
      package_name: productData?.name,
      company_package_id: productData?.company_product_id,
      company_id: authUser?.company_id,
      retailer_id: authUser?.id,
      amount:
        productData?.discount_price == null
          ? 0
          : Number(productData.discount_price),
    };

    await axios.post(
      `${BASE_URL}warranty/buy-subscription-with-offer`,
      payload,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );

    toast.success("Subscription activated successfully 🎉");

    // ✅ Update localStorage
    const updatedAuthUser = {
      ...authUser,
      is_free_subscribed: 1,
      is_subscribed: 1,
    };

    localStorage.setItem(
      "authUserData",
      JSON.stringify(updatedAuthUser)
    );

    navigate("/retproduct", {
      state: {
        activeTab: "subscribed",
      },
    });
  } catch (err) {
    console.error(err);

    toast.error(
      err?.response?.data?.message ||
      "Something went wrong"
    );
  } finally {
    setWalletPaymentLoading(false);
  }
};

const notesData = {
  company_id: authUser?.company_id,
  retailer_id: authUser?.id,
  package_id: productData?.id,
  company_package_id : productData?.company_product_id,
  service: "subscription",
};


const handlePayment = (
  amount,
  onSuccess
) => {

  if (!amount)
    return;

  if (
    !paymentKeys.key_id
  ) {

    alert(
      "Payment keys not loaded!"
    );

    return;
  }

  if (
    Number(amount) < 1000
  ) {

    toast.error(
      "Minimum amount is ₹1000"
    );

    return;
  }

  const options = {

    key:
      paymentKeys.key_id,

    amount:
      amount * 100,

    currency:
      "INR",

    name:
      "Warranty Mitra",
    
    image:
      "https://warrantymitra.com/img/favicon_io/favicon.ico",

    notes:
      notesData,

    handler: async function (
      response
    ) {

      setPaymentCompleted(
        true
      );

      setPaymentSaving(
        true
      );

      const payload = {

        company_id:
          authUser?.company_id,

        user_id:
          authUser?.id,

        payment_id:
          response.razorpay_payment_id,

        amount:
          amount,

        status:
          response,

        payment_date:
          new Date()
            .toISOString()
            .slice(0, 19)
            .replace(
              "T",
              " "
            ),

        // customer_id: zohoId || null,
        payment_from:
          "WARRANTY",

        role:
          authUser?.role,

        w_type:
          "advance_payment",
      };

      try {

        const responseData =
          await axios.post(
            `${BASE_URL}zoho/create-online-payment`,
            payload,
            {
              headers: {
                Accept:
                  "application/json",

                "Content-Type":
                  "application/json",
              },
            }
          );

        const data =
          responseData.data;

        toast.success(
          "Payment Successful & Saved in Zoho 🎉"
        );

        const updatedAuthUser = {
          ...authUser,
          // is_free_subscribed: 1,
          is_subscribed: 1,
        };

    localStorage.setItem(
      "authUserData",
      JSON.stringify(updatedAuthUser)
    );

        // fetchWalletBalance();
        // setWalletRefresh(prev => !prev);
        // setAddWalletModal(false);
        // setWalletAmount("");

        // 👇 close popup from child (desktop)
        navigate(
          "/retproduct",
          {
            state: {
              activeTab:
                "subscribed",
            },
          }
        );

      } catch (err) {

        console.error(
          err
        );

        toast.error(
          err?.response?.data
            ?.message ||
          "Payment succeeded but failed to update Zoho!"
        );

      } finally {

        setPaymentSaving(
          false
        );
      }
    },

    prefill: {

      name:
        authUser?.name ||
        "",

      email:
        authUser
          ?.contact_email ||
        "",

      contact:
        authUser
          ?.contact_phone ||
        "",
    },

    theme: {
      color:
        "#15803D",
    },
  };

  const rzp =
    new window.Razorpay(
      options
    );

  rzp.open();
};


const getIsRetailerMobile = () => {
  if (typeof window === "undefined") return false;

  const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");

  return (
    window.innerWidth < 640 &&
    (authUser?.role === 5 || authUser?.role === 6)
  );
};

const isRetailerMobile = getIsRetailerMobile();

  
if (loading || !PLAN) {
  return <OrderSummarySkeleton />;
}



  return (
    <div
        className="min-h-screen font-body"
        style={{
          background: isRetailerMobile
            ? "glass"
            : "#F8FAFC",
        }}
      >
      <div className="mx-auto p-2 lg:p-6">

        {/* Header */}
        <div className="flex items-center gap-3 mb-3 animate-fade-in">
          <div>
            <h1 className="font-display font-black text-[16px]"
              style={{ color:"var(--color-text-primary)" }}>
              Subscription Summary
            </h1>
            <p className="text-[11px] text-muted font-body">Review plan details and complete payment</p>
          </div>
        </div>

        {/* TWO-COLUMN LAYOUT — matches reference exactly */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">

          {/* ── LEFT PANEL (col-span 8) ── */}
          <div className="lg:col-span-8 flex flex-col gap-4">

            {/* Plan hero + price + enroll selector — all above the fold */}
            <div className="card p-1 animate-fade-in-up delay-1">
              <PlanHeroCard plan={PLAN}/>
              {/* <PriceBox plan={PLAN} selectedPlan={selectedPlan}/> */}
              {/* <Divider/> */}
              
            </div>

            {/* Plan overview + features — collapsible feel via layout */}
            <div className="animate-fade-in-up delay-2">
              <PlanOverviewSection plan={PLAN} isRetailerMobile={isRetailerMobile}/>
              <FeaturesSection plan={PLAN} isRetailerMobile={isRetailerMobile}/>

              <EnrollPlanSelector
                plans={ENROLL_PLANS}
                selected={selectedPlan}
                onSelect={setSelectedPlan}
              />
            </div>
          </div>

          {/* ── RIGHT PANEL (col-span 4) — sticky billing ── */}
          <div className="lg:col-span-4 animate-fade-in-up delay-3">
            <div className="sticky top-4">
              { PLAN?.show_popup !== 1 && (
                <BillingSummary
                plan={PLAN}
                selectedPlan={selectedPlan}
                paymentMethod={paymentMethod}
                onMethodChange={setPaymentMethod}
                walletAllowed={walletAllowed}
                walletBalance={walletBalance}
                handlePayment={handlePayment}   // ✅ ADD THIS
                handleWalletPayment = {handleWalletPayment}
                setShowWalletModal={setShowWalletModal}
                isRetailerMobile={isRetailerMobile}
              />
              )}

              { PLAN?.show_popup === 1 && (
                <BillingSummary1
                plan={PLAN}
                selectedPlan={selectedPlan}
                paymentMethod={paymentMethod}
                onMethodChange={setPaymentMethod}
                walletAllowed={walletAllowed}
                walletBalance={walletBalance}
                // handlePayment={handlePayment}   // ✅ ADD THIS
                // handleWalletPayment = {handleWalletPayment}

                handleOfferSubscription={handleOfferSubscription}
                setShowWalletModal={setShowWalletModal}
                isRetailerMobile={isRetailerMobile}
                loading={walletPaymentLoading} 
              />
              )}

            </div>
          </div>
        </div>
      </div>

      {showWalletModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">

          {/* <div className="w-[90%] max-w-md rounded-2xl p-6 bg-white shadow-xl animate-fade-in-up"> */}
          <div
            className={`w-[90%] max-w-md rounded-2xl p-6 animate-fade-in-up ${
              isRetailerMobile ? "glass" : "bg-white shadow-xl"
            }`}>
            
            {/* Title */}
            <h2 className={`text-base font-semibold  mb-2 ${
              isRetailerMobile ? "text-slate-200" : "text-gray-900"
            }`}>
              Confirm Payment
            </h2>

            {/* Message */}
            <p className={`text-sm  mb-5 ${
              isRetailerMobile ? "text-slate-400" : "text-gray-500"
            }`}>
              Are you sure you want to pay using your wallet balance?
            </p>

            {/* Amount box */}
            <div className="p-3 rounded-xl mb-5 flex justify-between items-center bg-blue-50 border border-blue-200">
              <span className="text-sm text-gray-600">Amount</span>
              <span className="font-semibold text-gray-900">
                ₹{productData?.mrp?.toLocaleString("en-IN")}
              </span>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              
              {/* Cancel */}
              <button
                onClick={() => setShowWalletModal(false)}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
              >
                Cancel
              </button>

              {/* Confirm */}
              <button
                disabled={walletPaymentLoading}
                onClick={() => {
                  handleWalletPayment();
                }}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {walletPaymentLoading ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// import { useEffect, useState } from "react";
// import axios from "axios";
// import { BASE_URL } from "../../../../config/api";
// import { motion, AnimatePresence } from "framer-motion";
// import { useNavigate, useLocation } from "react-router-dom";

// // ─── Utility ────────────────────────────────────────────────────────────────
// const fmt = (n) =>
//   Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

// // ─── Small sub-components ───────────────────────────────────────────────────

// function SectionLabel({ number, title }) {
//   return (
//     <div className="flex items-center gap-3 mb-4">
//       <span className="w-7 h-7 rounded-full bg-[#2233CC] text-white text-xs font-extrabold flex items-center justify-center flex-shrink-0">
//         {number}
//       </span>
//       <span className="text-sm font-bold text-slate-700 uppercase tracking-widest">
//         {title}
//       </span>
//     </div>
//   );
// }

// function InfoRow({ label, value, highlight }) {
//   return (
//     <div className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
//       <span className="text-sm text-slate-500">{label}</span>
//       <span className={`text-sm font-semibold ${highlight ? "text-[#2233CC]" : "text-slate-800"}`}>
//         {value}
//       </span>
//     </div>
//   );
// }

// /**
//  * Parses and displays listed items (Features, Coverage, etc.)
//  */
// function DataList({ data, icon, title, colorClass }) {
//   if (!data) return null;
  
//   // Logic to split by specific characters found in your JSON
//   const items = data
//     .split(/[✔.🚫]/)
//     .map(item => item.trim())
//     .filter(item => item.length > 0);

//   return (
//     <div className="mb-6">
//       <h4 className={`text-[11px] font-black uppercase tracking-wider mb-3 ${colorClass}`}>
//         {title}
//       </h4>
//       <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
//         {items.map((item, idx) => (
//           <div key={idx} className="flex items-start gap-2 group">
//             <span className="mt-1 text-xs opacity-70 group-hover:opacity-100 transition-opacity">
//                {icon}
//             </span>
//             <span className="text-xs text-slate-600 leading-tight">
//               {item}
//             </span>
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// }

// // ─── Product Summary Card (right panel) ─────────────────────────────────────
// function ProductSummaryCard({ product }) {
//   const category = product?.categories?.[0];

//   return (
//     <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-100 sticky top-6">
//       <div className="bg-[#2233CC] px-6 pt-6 pb-10 relative">
//         <div className="absolute top-4 right-4 w-20 h-20 rounded-full border border-white/10" />
//         <div className="mt-2">
//           <p className="text-white/60 text-xs uppercase tracking-widest mb-1">Warranty Plan</p>
//           <p className="text-white font-extrabold text-xl leading-tight">
//             { product?.product_type}
//           </p>
//           <p className="text-white/70 text-xs mt-1 line-clamp-2">{product?.name}</p>
//         </div>
//       </div>

//       <div className="px-6 py-5 space-y-1">
//         <div className="text-center mb-4">
//           <p className="text-lg font-extrabold text-slate-900">₹{fmt(product?.mrp)}</p>
//           <p className="text-xs text-slate-400 uppercase tracking-widest mt-1">Per Plan</p>
//         </div>
//         {/* <InfoRow label="HSN Code" value={product?.hsn_code || "—"} />
//         <InfoRow label="Claims" value={`${product?.claims} Allowed`} /> */}
//         <InfoRow label="Plan Validity" value={`${product?.sub_val_days} Days`} />
//         <InfoRow label="Enroll Count" value={`${product?.sold_count || 0} Devices`} />

//         <div className="flex justify-between items-center pt-3 mt-2 border-t border-slate-200">
//           <span className="font-extrabold text-slate-800 text-base">Total</span>
//           <span className="font-extrabold text-[#2233CC] text-md">₹{fmt(product?.mrp)}</span>
//         </div>
//       </div>
//     </div>
//   );
// }

// function WalletBanner({ balance, mrp }) {
//   const bal = Number(balance ?? 0);
//   const price = Number(mrp ?? 0);
//   const sufficient = bal >= price;

//   return (
//     <div className={`rounded-2xl px-4 py-3 flex items-center gap-3 ${sufficient ? "bg-emerald-50 border border-emerald-200" : "bg-amber-50 border border-amber-200"}`}>
//       <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${sufficient ? "bg-emerald-100" : "bg-amber-100"}`}>
//         <span className="text-base">{sufficient ? "💰" : "⚠️"}</span>
//       </div>
//       <div>
//         <p className={`text-xs font-bold ${sufficient ? "text-emerald-700" : "text-amber-700"}`}>
//           Wallet Balance: ₹{fmt(bal)}
//         </p>
//         <p className={`text-[11px] ${sufficient ? "text-emerald-600" : "text-amber-600"}`}>
//           {sufficient ? "Sufficient balance to pay via wallet" : "Low balance — please recharge to use wallet"}
//         </p>
//       </div>
//     </div>
//   );
// }

// function PayButton({ icon, label, sublabel, onClick, disabled, variant = "outline" }) {
//   return (
//     <button
//       onClick={onClick}
//       disabled={disabled}
//       className={`w-full flex items-center gap-4 px-5 py-4 rounded-2xl font-semibold transition-all duration-200 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed ${variant === "solid" ? "bg-[#2233CC] text-white hover:bg-blue-800 shadow-lg" : "border-2 border-[#2233CC] text-[#2233CC] hover:bg-blue-50"}`}
//     >
//       <span className={`text-2xl w-8 text-center`}>{icon}</span>
//       <div className="text-left">
//         <p className="text-sm font-extrabold leading-tight">{label}</p>
//         {sublabel && <p className={`text-[11px] mt-0.5 ${variant === "solid" ? "text-white/70" : "text-slate-400"}`}>{sublabel}</p>}
//       </div>
//       <span className="ml-auto text-lg opacity-60">→</span>
//     </button>
//   );
// }

// // ─── Main Checkout Page ──────────────────────────────────────────────────────
// export default function OrderCheckoutPage() {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const product = location.state?.product;

//   const [userData, setUserData] = useState(null);
//   const [loadingUser, setLoadingUser] = useState(true);
//   const [paying, setPaying] = useState(false);
//   const [paidMethod, setPaidMethod] = useState(null);

//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = authUserString ? JSON.parse(authUserString) : null;

//   useEffect(() => {
//     async function fetchUser() {
//       if (!authUser?.id) return;
//       try {
//         const res = await axios.get(`${BASE_URL}common/get-users`, { params: { id: authUser.id } });
//         setUserData(res.data?.data?.[0] || null);
//       } catch (e) {
//         console.error("User fetch error", e);
//       } finally {
//         setLoadingUser(false);
//       }
//     }
//     fetchUser();
//   }, []);

//   if (!product) {
//     return (
//       <div className="flex flex-col items-center justify-center min-h-screen gap-4 text-slate-500">
//         <p className="text-sm">No product selected.</p>
//         <button onClick={() => navigate(-1)} className="text-sm text-blue-600 font-semibold underline">Go back</button>
//       </div>
//     );
//   }

//   const walletBalance = Number(userData?.wallet_balance ?? 0);
//   const mrp = Number(product?.mrp ?? 0);
//   const walletSufficient = userData !== null && walletBalance >= mrp;
//   const hasWallet = userData !== null && userData.wallet_balance !== null;

//   const handlePay = async (method) => {
//     setPaying(true);
//     await new Promise((r) => setTimeout(r, 1200)); 
//     setPaidMethod(method);
//     setPaying(false);
//   };

//   if (paidMethod) {
//     return (
//       <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
//         <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-sm w-full text-center">
//           <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4 text-emerald-600 font-bold text-2xl">✓</div>
//           <h2 className="text-xl font-extrabold text-slate-800 mb-2">Order Placed!</h2>
//           <p className="text-sm text-slate-500 mb-6">{product?.name}</p>
//           <button onClick={() => navigate(-1)} className="w-full py-3 rounded-2xl bg-[#2233CC] text-white font-extrabold text-sm uppercase tracking-widest">Back to Products</button>
//         </div>
//       </motion.div>
//     );
//   }

//   return (
//     <div className="min-h-screen bg-slate-100">
//       <div className="px-6 pb-12 mx-auto max-w-7xl">
//         <div className="mb-8 mt-6">
//           <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">Subscription Summary</h1>
//           <p className="text-slate-400 text-sm mt-1">Review plan details and complete payment</p>
//         </div>

//         <div className="flex flex-col lg:flex-row gap-8 items-start">
//             {/* Right Summary Panel */}
//           <motion.div 
//             initial={{ opacity: 0, x: 20 }} 
//             animate={{ opacity: 1, x: 0 }} 
//             transition={{ delay: 0.3 }} 
//             // 1. h-fit ensures the div only takes up as much space as the card
//             // 2. sticky and top-6 keep it in view
//             className="w-full lg:w-80 flex-shrink-0 lg:sticky lg:top-6 h-fit"
//             >
//             <ProductSummaryCard product={product} />
//             </motion.div>
//           <div className="flex-1 space-y-4">
            
//             {/* Section 2: Plan Details */}
//             <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6">
//               <SectionLabel number="1" title="Plan Overview" />
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
//                 <InfoRow label="Product Type" value={product.product_type} />
//                 <InfoRow label="Claims" value={`${product.claims} Claims`} />
//                 <InfoRow label="Product Validity" value={`${product.validity} Days`} />
//                 {/* <InfoRow label="Max Enrollment" value={product.enroll_max} /> */}
//                 <InfoRow label="Price Type" value={product.is_fixed === 1 ? "Fixed" : "Variable"} />
//                 {/* <InfoRow label="HSN" value={product.hsn_code} /> */}
//               </div>
//             </motion.div>

//             {/* Section 4: ALL PRODUCT DATA (Features, Coverage, Exclusions) */}
//             <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6">
//               <SectionLabel number="2" title="Plan Coverage & Features" />
              
//               <DataList 
//                 title="What's Included (Features)" 
//                 data={product.features} 
//                 icon="⭐" 
//                 colorClass="text-blue-600"
//               />
              
//               <hr className="my-6 border-slate-100" />
              
//               <DataList 
//                 title="Service Coverage" 
//                 data={product.coverage} 
//                 icon="🛡️" 
//                 colorClass="text-emerald-600"
//               />

//               <hr className="my-6 border-slate-100" />

//               {/* <DataList 
//                 title="Exclusions (What's not covered)" 
//                 data={product.exclusions} 
//                 icon="❌" 
//                 colorClass="text-rose-600"
//               /> */}
//             </motion.div>

//             {/* Section 3: Payment */}
//             <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6">
//               <SectionLabel number="3" title="Payment Method" />
//               {hasWallet && !loadingUser && <div className="mb-5"><WalletBanner balance={walletBalance} mrp={mrp} /></div>}
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
//                 {hasWallet && (
//                   <PayButton
//                     icon="👛"
//                     label="Wallet Pay"
//                     sublabel={walletSufficient ? `Pay ₹${fmt(mrp)} from balance` : "Top up required"}
//                     variant={walletSufficient ? "solid" : "outline"}
//                     disabled={!walletSufficient || paying}
//                     onClick={() => handlePay("wallet")}
//                   />
//                 )}
//                 {(userData?.pay_now === 1 || !userData) && (
//                   <PayButton icon="💳" label="Pay Online" sublabel="UPI / Card / NetBanking" disabled={paying} onClick={() => handlePay("online")} />
//                 )}
//               </div>
//             </motion.div>
//           </div>

          
//         </div>
//       </div>
//     </div>
//   );
// }


// import { useEffect, useState } from "react";
// import axios from "axios";
// import { BASE_URL } from "../../../../config/api";
// import { motion, AnimatePresence } from "framer-motion";
// import { useNavigate, useLocation } from "react-router-dom";

// // ─── Utility ────────────────────────────────────────────────────────────────
// const fmt = (n) =>
//   Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

// // ─── Small sub-components ───────────────────────────────────────────────────

// function SectionLabel({ number, title }) {
//   return (
//     <div className="flex items-center gap-3 mb-4">
//       <span className="w-7 h-7 rounded-full bg-[#2233CC] text-white text-xs font-extrabold flex items-center justify-center flex-shrink-0">
//         {number}
//       </span>
//       <span className="text-sm font-bold text-slate-700 uppercase tracking-widest">
//         {title}
//       </span>
//     </div>
//   );
// }

// function InfoRow({ label, value, highlight }) {
//   return (
//     <div className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
//       <span className="text-sm text-slate-500">{label}</span>
//       <span className={`text-sm font-semibold ${highlight ? "text-[#2233CC]" : "text-slate-800"}`}>
//         {value}
//       </span>
//     </div>
//   );
// }

// // ─── Product Summary Card (right panel) ─────────────────────────────────────
// function ProductSummaryCard({ product, user }) {
//   const category = product?.categories?.[0];

//   return (
//     <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-100">
//       {/* Blue header */}
//       <div className="bg-[#2233CC] px-6 pt-6 pb-10 relative">
//         {/* decorative circles */}
//         <div className="absolute top-4 right-4 w-20 h-20 rounded-full border border-white/10" />
//         <div className="absolute top-8 right-8 w-10 h-10 rounded-full border border-white/10" />

//         <div className="flex justify-between items-start mb-4">
//           <div className="bg-white/20 rounded-xl p-2">
//             <svg width="28" height="20" viewBox="0 0 28 20" fill="none">
//               <rect width="28" height="20" rx="4" fill="white" fillOpacity="0.3" />
//               <rect x="1" y="7" width="26" height="5" fill="white" fillOpacity="0.5" />
//             </svg>
//           </div>
//           <div className="bg-white/20 rounded-xl p-2">
//             <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
//               <circle cx="7" cy="10" r="6" fill="white" fillOpacity="0.6" />
//               <circle cx="13" cy="10" r="6" fill="white" fillOpacity="0.3" />
//             </svg>
//           </div>
//         </div>

//         <div className="mt-2">
//           <p className="text-white/60 text-xs uppercase tracking-widest mb-1">Warranty Plan</p>
//           <p className="text-white font-extrabold text-xl leading-tight">
//             {category?.name || product?.product_type}
//           </p>
//           <p className="text-white/70 text-xs mt-1 line-clamp-1">{product?.name}</p>
//         </div>

//         <div className="flex justify-between items-end mt-4">
//           <div>
//             <p className="text-white/50 text-[10px] uppercase tracking-widest">Validity</p>
//             <p className="text-white font-bold text-sm">{product?.sub_val_days} Days</p>
//           </div>
//           <div className="text-right">
//             <p className="text-white/50 text-[10px] uppercase tracking-widest">Devices</p>
//             <p className="text-white font-bold text-sm">{product?.enroll_max}</p>
//           </div>
//         </div>
//       </div>

//       {/* Manage Plans link */}
//       <div className="flex justify-between items-center px-6 py-3 border-b border-slate-100">
//         <span className="text-xs text-slate-500 font-medium">Manage Plans</span>
//         <span className="text-xs text-[#2233CC] font-bold">→</span>
//       </div>

//       {/* Price breakdown */}
//       <div className="px-6 py-5 space-y-1">
//         <div className="text-center mb-4">
//           <p className="text-4xl font-extrabold text-slate-900">
//             ₹{fmt(product?.mrp)}
//           </p>
//           <p className="text-xs text-slate-400 uppercase tracking-widest mt-1">Per Plan</p>
//         </div>

//         <InfoRow label="Plan MRP" value={`₹${fmt(product?.mrp)}`} />
//         <InfoRow label="HSN Code" value={product?.hsn_code || "—"} />
//         <InfoRow label="Claims Allowed" value={`${product?.claims}`} />
//         {/* {product?.margin > 0 && (
//           <InfoRow label="Your Margin" value={`₹${fmt(product?.margin)}`} highlight />
//         )} */}

//         <div className="flex justify-between items-center pt-3 mt-2 border-t border-slate-200">
//           <span className="font-extrabold text-slate-800 text-base">Total</span>
//           <span className="font-extrabold text-[#2233CC] text-xl">₹{fmt(product?.mrp)}</span>
//         </div>
//       </div>
//     </div>
//   );
// }

// // ─── Wallet Status Banner ────────────────────────────────────────────────────
// function WalletBanner({ balance, mrp }) {
//   const bal = Number(balance ?? 0);
//   const price = Number(mrp ?? 0);
//   const sufficient = bal >= price;

//   return (
//     <div
//       className={`rounded-2xl px-4 py-3 flex items-center gap-3
//         ${sufficient
//           ? "bg-emerald-50 border border-emerald-200"
//           : "bg-amber-50 border border-amber-200"
//         }`}
//     >
//       <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0
//         ${sufficient ? "bg-emerald-100" : "bg-amber-100"}`}>
//         <span className="text-base">{sufficient ? "💰" : "⚠️"}</span>
//       </div>
//       <div>
//         <p className={`text-xs font-bold ${sufficient ? "text-emerald-700" : "text-amber-700"}`}>
//           Wallet Balance: ₹{fmt(bal)}
//         </p>
//         <p className={`text-[11px] ${sufficient ? "text-emerald-600" : "text-amber-600"}`}>
//           {sufficient
//             ? "Sufficient balance to pay via wallet"
//             : "Low balance — please recharge to use wallet"}
//         </p>
//       </div>
//     </div>
//   );
// }

// // ─── Payment Method Button ───────────────────────────────────────────────────
// function PayButton({ icon, label, sublabel, onClick, disabled, variant = "outline" }) {
//   return (
//     <button
//       onClick={onClick}
//       disabled={disabled}
//       className={`w-full flex items-center gap-4 px-5 py-4 rounded-2xl font-semibold
//         transition-all duration-200 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed
//         ${variant === "solid"
//           ? "bg-[#2233CC] text-white hover:bg-blue-800 shadow-lg"
//           : "border-2 border-[#2233CC] text-[#2233CC] hover:bg-blue-50"
//         }`}
//     >
//       <span className={`text-2xl w-8 text-center`}>{icon}</span>
//       <div className="text-left">
//         <p className="text-sm font-extrabold leading-tight">{label}</p>
//         {sublabel && (
//           <p className={`text-[11px] mt-0.5 ${variant === "solid" ? "text-white/70" : "text-slate-400"}`}>
//             {sublabel}
//           </p>
//         )}
//       </div>
//       <span className="ml-auto text-lg opacity-60">→</span>
//     </button>
//   );
// }

// // ─── Main Checkout Page ──────────────────────────────────────────────────────
// export default function OrderCheckoutPage() {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const product = location.state?.product;

//   const [userData, setUserData] = useState(null);
//   const [loadingUser, setLoadingUser] = useState(true);
//   const [paying, setPaying] = useState(false);
//   const [paidMethod, setPaidMethod] = useState(null); // "wallet" | "online"

//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = authUserString ? JSON.parse(authUserString) : null;

//   useEffect(() => {
//     async function fetchUser() {
//       if (!authUser?.id) return;
//       try {
//         const res = await axios.get(`${BASE_URL}common/get-users`, {
//           params: { id: authUser.id },
//         });
//         setUserData(res.data?.data?.[0] || null);
//       } catch (e) {
//         console.error("User fetch error", e);
//       } finally {
//         setLoadingUser(false);
//       }
//     }
//     fetchUser();
//   }, []);

//   if (!product) {
//     return (
//       <div className="flex flex-col items-center justify-center min-h-screen gap-4 text-slate-500">
//         <p className="text-sm">No product selected.</p>
//         <button
//           onClick={() => navigate(-1)}
//           className="text-sm text-blue-600 font-semibold underline"
//         >
//           Go back
//         </button>
//       </div>
//     );
//   }

//   const walletBalance = Number(userData?.wallet_balance ?? 0);
//   const mrp = Number(product?.mrp ?? 0);
//   const walletSufficient = userData !== null && walletBalance >= mrp;
//   const hasWallet = userData !== null && userData.wallet_balance !== null;

//   const handlePay = async (method) => {
//     setPaying(true);
//     try {
//       // TODO: replace with actual payment API call
//       // await axios.post(`${BASE_URL}warranty/create-order`, {
//       //   product_id: product.id,
//       //   company_product_id: product.company_product_id,
//       //   payment_method: method,
//       // });
//       await new Promise((r) => setTimeout(r, 1200)); // simulate
//       setPaidMethod(method);
//     } catch (e) {
//       console.error("Payment error", e);
//     } finally {
//       setPaying(false);
//     }
//   };

//   // ── Success screen ──
//   if (paidMethod) {
//     return (
//       <motion.div
//         initial={{ opacity: 0, scale: 0.95 }}
//         animate={{ opacity: 1, scale: 1 }}
//         className="min-h-screen bg-slate-100 flex items-center justify-center p-6"
//       >
//         <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-sm w-full text-center">
//           <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
//             <span className="text-3xl">✓</span>
//           </div>
//           <h2 className="text-xl font-extrabold text-slate-800 mb-2">Order Placed!</h2>
//           <p className="text-sm text-slate-500 mb-1">
//             Paid via {paidMethod === "wallet" ? "Wallet" : "Online Payment"}
//           </p>
//           <p className="text-sm text-slate-500 mb-6">
//             {product?.name}
//           </p>
//           <button
//             onClick={() => navigate(-1)}
//             className="w-full py-3 rounded-2xl bg-[#2233CC] text-white font-extrabold text-sm uppercase tracking-widest"
//           >
//             Back to Products
//           </button>
//         </div>
//       </motion.div>
//     );
//   }

//   return (
//     <div className="min-h-screen bg-slate-100">

//       <div className="px-6 pb-5 mx-auto">
//         {/* Page title */}
//         <div className="mb-4 mt-4">
//           <h1 className="text-lg font-extrabold text-slate-900 tracking-tight">Subscription Summery</h1>
//           <p className="text-slate-400 text-xs mt-1">
//             Review your plan and choose how you'd like to pay
//           </p>
//         </div>

//         <div className="flex flex-col lg:flex-row gap-8 items-start">

//           {/* ── LEFT: Form ── */}
//           <div className="flex-1 space-y-3">

           

//             {/* 2. Plan Details */}
//             <motion.div
//               initial={{ opacity: 0, y: 20 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ delay: 0.1 }}
//               className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6"
//             >
//               <SectionLabel number="2" title="Plan Details" />
//               <div className="space-y-0">
//                 <InfoRow label="Plan Type" value={product.product_type} />
//                 <InfoRow label="Validity" value={`${product.sub_val_days} Days`} />
//                 <InfoRow label="Max Devices" value={product.enroll_max} />
//                 <InfoRow label="Claims" value={product.claims} />
//                 {product.min_value && (
//                   <InfoRow
//                     label="Device Value Range"
//                     value={`₹${fmt(product.min_value)} – ₹${fmt(product.max_value)}`}
//                   />
//                 )}
//                 <InfoRow label="HSN Code" value={product.hsn_code || "—"} />
//               </div>
//             </motion.div>

//             {/* 3. Payment Method */}
//             <motion.div
//               initial={{ opacity: 0, y: 20 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ delay: 0.15 }}
//               className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6"
//             >
//               <SectionLabel number="3" title="Payment Method" />

//               {/* Wallet banner */}
//               {hasWallet && !loadingUser && (
//                 <div className="mb-5">
//                   <WalletBanner balance={walletBalance} mrp={mrp} />
//                 </div>
//               )}

//               <div className="space-y-3">
//                 {/* Wallet Pay */}
//                 {hasWallet && (
//                   <PayButton
//                     icon="👛"
//                     label="Pay by Wallet"
//                     sublabel={
//                       walletSufficient
//                         ? `Balance: ₹${fmt(walletBalance)}`
//                         : `Insufficient — ₹${fmt(walletBalance)} available`
//                     }
//                     variant={walletSufficient ? "solid" : "outline"}
//                     disabled={!walletSufficient || paying}
//                     onClick={() => handlePay("wallet")}
//                   />
//                 )}

//                 {/* Pay Now (online) — shown only if pay_now is enabled */}
//                 {(userData?.pay_now === 1 || !userData) && (
//                   <PayButton
//                     icon="💳"
//                     label="Pay Now"
//                     sublabel="UPI / Card / Net Banking"
//                     variant="outline"
//                     disabled={paying}
//                     onClick={() => handlePay("online")}
//                   />
//                 )}

//                 {/* Pay Later — shown only if pay_later is enabled */}
//                 {/* {userData?.pay_later === 1 && (
//                   <PayButton
//                     icon="🕐"
//                     label="Pay Later"
//                     sublabel="Deferred payment"
//                     variant="outline"
//                     disabled={paying}
//                     onClick={() => handlePay("later")}
//                   />
//                 )} */}
//               </div>

//               {paying && (
//                 <div className="flex items-center justify-center gap-3 mt-4 text-slate-500 text-sm">
//                   <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
//                   Processing payment…
//                 </div>
//               )}
//             </motion.div>
//           </div>

//           {/* ── RIGHT: Summary card ── */}
//           <motion.div
//             initial={{ opacity: 0, x: 20 }}
//             animate={{ opacity: 1, x: 0 }}
//             transition={{ delay: 0.2 }}
//             className="w-full lg:w-80 flex-shrink-0"
//           >
//             <ProductSummaryCard product={product} user={userData} />
//           </motion.div>

//         </div>
//       </div>
//     </div>
//   );
// }